import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ShopifyProductPicker from './components/Shopify/ShopifyProductPicker';
import { toast, ToastContainer } from 'react-toastify';
import { FaRegImage, FaMagic } from 'react-icons/fa';
import 'react-toastify/dist/ReactToastify.css';
import { FaBullseye } from 'react-icons/fa';
import { FaCreditCard, FaBriefcase, FaHome } from 'react-icons/fa';
import { fetchAudienceEstimate } from '../../helpers/adFunctions';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { FaHeart } from 'react-icons/fa';
import {
  FaRoad, FaShoppingCart, FaMobileAlt, FaClipboardList,
  FaBroadcastTower, FaBullhorn, FaThumbsUp,
  FaLinkedin, FaShopify
} from 'react-icons/fa';
import { getRequest, postRequest } from '../../services/api/apiService';
import { PLATFORMS, isChannelConnected } from '../../constants/platforms';
import { useCreateAdContext } from '../../context/CreateAdContext';

const locationOptions = [
  { label: 'North Africa', options: [
    { value: 'MA', label: 'Morocco' },
    { value: 'EG', label: 'Egypt' },
    { value: 'DZ', label: 'Algeria' },
    { value: 'TN', label: 'Tunisia' },
    { value: 'LY', label: 'Libya' },
  ]},
  { label: 'Middle East', options: [
    { value: 'SA', label: 'Saudi Arabia' },
    { value: 'AE', label: 'UAE' },
    { value: 'QA', label: 'Qatar' },
    { value: 'KW', label: 'Kuwait' },
    { value: 'BH', label: 'Bahrain' },
    { value: 'OM', label: 'Oman' },
    { value: 'JO', label: 'Jordan' },
    { value: 'LB', label: 'Lebanon' },
    { value: 'IQ', label: 'Iraq' },
  ]},
  { label: 'West Africa', options: [
    { value: 'SN', label: 'Senegal' },
    { value: 'CI', label: "Côte d'Ivoire" },
    { value: 'GH', label: 'Ghana' },
    { value: 'NG', label: 'Nigeria' },
    { value: 'CM', label: 'Cameroon' },
    { value: 'TG', label: 'Togo' },
    { value: 'BJ', label: 'Benin' },
    { value: 'ML', label: 'Mali' },
  ]},
  { label: 'Europe', options: [
    { value: 'FR', label: 'France' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'DE', label: 'Germany' },
    { value: 'ES', label: 'Spain' },
    { value: 'IT', label: 'Italy' },
    { value: 'NL', label: 'Netherlands' },
    { value: 'BE', label: 'Belgium' },
    { value: 'PT', label: 'Portugal' },
    { value: 'CH', label: 'Switzerland' },
    { value: 'SE', label: 'Sweden' },
    { value: 'NO', label: 'Norway' },
    { value: 'DK', label: 'Denmark' },
    { value: 'PL', label: 'Poland' },
    { value: 'TR', label: 'Turkey' },
    { value: 'AT', label: 'Austria' },
    { value: 'IE', label: 'Ireland' },
  ]},
  { label: 'Americas', options: [
    { value: 'US', label: 'United States' },
    { value: 'CA', label: 'Canada' },
    { value: 'MX', label: 'Mexico' },
    { value: 'BR', label: 'Brazil' },
    { value: 'AR', label: 'Argentina' },
    { value: 'CO', label: 'Colombia' },
    { value: 'CL', label: 'Chile' },
  ]},
  { label: 'Asia-Pacific', options: [
    { value: 'AU', label: 'Australia' },
    { value: 'NZ', label: 'New Zealand' },
    { value: 'IN', label: 'India' },
    { value: 'JP', label: 'Japan' },
    { value: 'SG', label: 'Singapore' },
    { value: 'ID', label: 'Indonesia' },
    { value: 'MY', label: 'Malaysia' },
    { value: 'PH', label: 'Philippines' },
    { value: 'TH', label: 'Thailand' },
    { value: 'KR', label: 'South Korea' },
    { value: 'HK', label: 'Hong Kong' },
  ]},
];

const moroccoCitiesOptions = [
  { value: '2553604', label: 'Casablanca' },
  { value: '2537763', label: 'Rabat' },
  { value: '2542978', label: 'Marrakech' },
  { value: '2548885', label: 'Fes' },
  { value: '2527087', label: 'Tangier' },
  { value: '2566226', label: 'Agadir' },
  { value: '2542601', label: 'Meknes' },
  { value: '2529177', label: 'Oujda' },
  { value: '2543831', label: 'Kenitra' },
  { value: '2527084', label: 'Tetouan' },
  { value: '2548453', label: 'El Jadida' },
  { value: '2542309', label: 'Mohammedia' },
  { value: '2537775', label: 'Safi' },
  { value: '2550467', label: 'Béni Mellal' },
  { value: '2542332', label: 'Nador' },
];

const languageOptions = [
  { value: 'ar', label: 'Arabic' },
  { value: 'fr', label: 'French' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'nl', label: 'Dutch' },
  { value: 'tr', label: 'Turkish' },
  { value: 'wo', label: 'Wolof' },
  { value: 'ha', label: 'Hausa' },
  { value: 'id', label: 'Indonesian' },
  { value: 'ms', label: 'Malay' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'zh', label: 'Chinese (Simplified)' },
  { value: 'hi', label: 'Hindi' },
];

// ── Static options (placements, LinkedIn) ──────────────────────────────────────

const metaPlacementOptions = [
  { label: 'Facebook', options: [
    { value: 'feed', label: 'Facebook Feed' },
    { value: 'right_hand_column', label: 'Facebook Right Column' },
    { value: 'marketplace', label: 'Facebook Marketplace' },
    { value: 'video_feeds', label: 'Facebook Video Feed' },
    { value: 'story', label: 'Facebook Stories' },
    { value: 'search', label: 'Facebook Search' },
    { value: 'reels', label: 'Facebook Reels' },
  ]},
  { label: 'Instagram', options: [
    { value: 'instagram_feed', label: 'Instagram Feed' },
    { value: 'instagram_story', label: 'Instagram Stories' },
    { value: 'instagram_explore', label: 'Instagram Explore' },
    { value: 'instagram_reels', label: 'Instagram Reels' },
    { value: 'instagram_shop', label: 'Instagram Shop' },
  ]},
  { label: 'Audience Network', options: [
    { value: 'an_classic', label: 'Audience Network' },
    { value: 'an_interstitial', label: 'Audience Network Interstitial' },
  ]},
  { label: 'Messenger', options: [
    { value: 'messenger_inbox', label: 'Messenger Inbox' },
    { value: 'messenger_stories', label: 'Messenger Stories' },
  ]},
];

const linkedinSeniorityOptions = [
  { value: '1', label: 'Unpaid' },
  { value: '2', label: 'Training' },
  { value: '3', label: 'Entry' },
  { value: '4', label: 'Senior' },
  { value: '5', label: 'Manager' },
  { value: '6', label: 'Director' },
  { value: '7', label: 'VP' },
  { value: '8', label: 'CXO' },
  { value: '9', label: 'Partner' },
  { value: '10', label: 'Owner' },
];

const linkedinIndustryOptions = [
  { value: '1', label: 'Technology & Software' },
  { value: '3', label: 'Finance' },
  { value: '4', label: 'Education' },
  { value: '6', label: 'Healthcare' },
  { value: '7', label: 'Manufacturing' },
  { value: '11', label: 'Retail' },
  { value: '12', label: 'Telecommunications' },
  { value: '14', label: 'Media & Entertainment' },
  { value: '16', label: 'Real Estate' },
  { value: '19', label: 'Marketing & Advertising' },
  { value: '26', label: 'E-commerce' },
  { value: '27', label: 'Food & Beverages' },
  { value: '28', label: 'Transportation & Logistics' },
  { value: '30', label: 'Legal Services' },
  { value: '31', label: 'Consulting' },
];

const linkedinJobFunctionOptions = [
  { value: '1', label: 'Accounting' },
  { value: '2', label: 'Administrative' },
  { value: '3', label: 'Arts & Design' },
  { value: '4', label: 'Business Development' },
  { value: '5', label: 'Community & Social Services' },
  { value: '6', label: 'Consulting' },
  { value: '7', label: 'Education' },
  { value: '8', label: 'Engineering' },
  { value: '9', label: 'Entrepreneurship' },
  { value: '10', label: 'Finance' },
  { value: '11', label: 'Healthcare Services' },
  { value: '12', label: 'Human Resources' },
  { value: '13', label: 'Information Technology' },
  { value: '14', label: 'Legal' },
  { value: '15', label: 'Marketing' },
  { value: '16', label: 'Media & Communications' },
  { value: '17', label: 'Military & Protective Services' },
  { value: '18', label: 'Operations' },
  { value: '19', label: 'Product Management' },
  { value: '20', label: 'Program & Project Management' },
  { value: '21', label: 'Purchasing' },
  { value: '22', label: 'Quality Assurance' },
  { value: '23', label: 'Real Estate' },
  { value: '24', label: 'Research' },
  { value: '25', label: 'Sales' },
];

const linkedinCompanySizeOptions = [
  { value: 'A', label: 'Self-employed' },
  { value: 'B', label: '1-10 employees' },
  { value: 'C', label: '11-50 employees' },
  { value: 'D', label: '51-200 employees' },
  { value: 'E', label: '201-500 employees' },
  { value: 'F', label: '501-1000 employees' },
  { value: 'G', label: '1001-5000 employees' },
  { value: 'H', label: '5001-10000 employees' },
  { value: 'I', label: '10001+ employees' },
];

const tiktokInterestOptions = [
  { value: '23|2', label: 'Apparel & Accessories' },
  { value: '23|3', label: 'Auto & Vehicles' },
  { value: '23|4', label: 'Baby, Kids & Maternity' },
  { value: '23|5', label: 'Beauty & Personal Care' },
  { value: '23|6', label: 'Books & Literature' },
  { value: '23|7', label: 'Business & Finance' },
  { value: '23|8', label: 'Computers & Electronics' },
  { value: '23|9', label: 'Education' },
  { value: '23|10', label: 'Entertainment' },
  { value: '23|11', label: 'Food & Beverage' },
  { value: '23|13', label: 'Gaming' },
  { value: '23|14', label: 'Health & Fitness' },
  { value: '23|15', label: 'Home & Garden' },
  { value: '23|16', label: 'Music' },
  { value: '23|17', label: 'News & Politics' },
  { value: '23|18', label: 'Sports & Outdoors' },
  { value: '23|19', label: 'Travel' },
  { value: '23|20', label: 'Pets & Animals' },
  { value: '23|21', label: 'Photography' },
  { value: '23|22', label: 'Real Estate' },
  { value: '23|23', label: 'Religion & Spirituality' },
  { value: '23|24', label: 'Science & Nature' },
  { value: '23|25', label: 'Shopping' },
  { value: '23|26', label: 'Sports & Fitness' },
];

// Allow-list checked before any file reaches Cloudinary (BUG-015 / CT-094).
// No backend sits in this upload path (browser → Cloudinary directly), so this
// client-side check is the only gate available — both extension AND the
// browser-reported MIME type must match, closing the "just ignore accept=""" gap.
const ALLOWED_FILE_TYPES = {
  image: { mimes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'], extensions: ['.jpg', '.jpeg', '.png', '.webp', '.gif'] },
  video: { mimes: ['video/mp4', 'video/quicktime', 'video/webm'], extensions: ['.mp4', '.mov', '.webm'] },
  videothumbnail: { mimes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'], extensions: ['.jpg', '.jpeg', '.png', '.webp', '.gif'] },
};

const isAllowedFile = (file, mediaType) => {
  const rule = ALLOWED_FILE_TYPES[mediaType];
  if (!rule) return false;
  const nameLower = (file.name || '').toLowerCase();
  const extOk = rule.extensions.some((ext) => nameLower.endsWith(ext));
  const mimeOk = rule.mimes.includes(file.type);
  return extOk && mimeOk;
};

const DRAFT_STORAGE_KEY = 'onebotads:createAd:draft';

const loadDraft = () => {
  try {
    const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const CreateAd = ({ isPremium, shopifyProduct, initialPlatform, aiMode = false, onSwitchToAI, onSwitchToManual }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pendingActions, clearActions, submitSignal } = useCreateAdContext();
  const [shopifyConnected, setShopifyConnected] = useState(false);
  const [shopifyModalOpen, setShopifyModalOpen] = useState(false);
  const [localShopifyProduct, setLocalShopifyProduct] = useState(null);
  const effectiveProduct = shopifyProduct || location.state?.shopifyProduct || localShopifyProduct || null;
  const [highlightedFields, setHighlightedFields] = useState(new Set());
  const [isSimpleCampaign, setIsSimpleCampaign] = useState(true);
  const [showCampaign, setShowCampaign] = useState(true);
  const [showAdset, setShowAdset] = useState(false);
  const [showAd, setShowAd] = useState(false);
  const [promotedObjectFields, setPromotedObjectFields] = useState([]);
  const [metaData, setMetaData] = useState({ businessId: '', pages: [], apps: [], pixels: [], instagramProfiles: [] });
  const [metaAdAccounts, setMetaAdAccounts] = useState([]);
  const [tiktokAdAccounts, setTiktokAdAccounts] = useState([]);
  const [linkedInAdAccounts, setLinkedInAdAccounts] = useState([]);
  const [snapchatAdAccounts, setSnapchatAdAccounts] = useState([]);
  const [googleAdAccounts, setGoogleAdAccounts] = useState([]);
  const [connectedPlatforms, setConnectedPlatforms] = useState(new Set());
  const [selectedAccessToken, setSelectedAccessToken] = useState('');
  const [audienceEstimate, setAudienceEstimate] = useState(null);
  const [selectedAdAccount, setSelectedAdAccount] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [tiktokInterestCategories, setTiktokInterestCategories] = useState(tiktokInterestOptions);


  
  // ── Async loader for Meta Interests (calls backend → Graph API search) ──────
  const loadMetaInterests = useCallback(async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];
    try {
      const res = await getRequest(`/api/ads/meta/interests?q=${encodeURIComponent(inputValue)}`);
      return (res?.data || []).map((i) => ({
        value: i.id,
        label: i.audience_size
          ? `${i.name} (~${Number(i.audience_size).toLocaleString()} people)`
          : i.name,
        id: i.id,
        name: i.name,
      }));
    } catch {
      return [];
    }
  }, []);

  const [formData, setFormData] = useState(() => {
  const defaults = {
    name: '',
    objective: 'OUTCOME_TRAFFIC',
    status: 'PAUSED',
    category: [],
    publisherPlatform: [],
    totalBudget: 0,
    smartBudget: 0,
    dayparting: 'All Day',
    specificTimes: '',
    gender: 'All',
    optimization_goal: 'LINK_CLICKS',
    age: [],
    languages: [],
    location: ['MA'],
    cities: [],
    adSetName: '',
    billingEvent: 'LINK_CLICKS',
    spendingPower: 'Low',
    startTime: '',
    endTime: '',
    frequency_cap: 0,
    frequency_cap_reset_period: 0,
    frequency_cap_time_unit: 'DAY',
    creativeType: 'Image',
    adCreativeName: '',
    imageUrl: '',
    videoUrl: '',
    videoThumbnailUrl: '',
    adTitle: '',
    adText: '',
    description: '',
    callToAction: '',
    destinationUrl: '',
    budgetTypeMeta: 'Daily',
    currencyMeta: 'MAD',
    budgetMeta: 0,
    budgetTypeTikTok: 'Daily',
    currencyTikTok: 'MAD',
    budgetTikTok: 0,
    budgetTypeGoogle: 'Daily',
    currencyGoogle: 'MAD',
    budgetGoogle: 0,
    budgetTypeSnapchat: 'Daily',
    currencySnapchat: 'MAD',
    budgetSnapchat: 0,
    budgetTypeLinkedIn: 'Daily',
    currencyLinkedIn: 'MAD',
    budgetLinkedIn: 0,
    costTypeLinkedIn: 'CPM',
    budgetTypeShopify: 'Daily',
    currencyShopify: 'MAD',
    budgetShopify: 0,
    budgetTaboola: 0,
    cpcTaboola: 0.5,
    bidStrategyTaboola: 'FIXED',
    marketingObjectiveTaboola: 'DRIVE_WEBSITE_TRAFFIC',
    cpaGoalTaboola: '',
    // Interests (Meta + TikTok)
    interests: [],
    // Placements
    placements: [],
    // LinkedIn-specific targeting
    linkedinSeniorities: [],
    linkedinIndustries: [],
    linkedinJobFunctions: [],
    linkedinCompanySizes: [],
    // Bid strategy + placement
    bid_strategy: 'LOWEST_COST',
    tiktokPlacement: 'PLACEMENT_TYPE_AUTOMATIC',
  };
  const draft = loadDraft();
  return draft ? { ...defaults, ...draft } : defaults;
});

useEffect(() => {
  const timeout = setTimeout(() => {
    try {
      sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(formData));
    } catch {}
  }, 300);
  return () => clearTimeout(timeout);
}, [formData]);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiAspectRatio, setAiAspectRatio] = useState('1:1');
  const [generating, setGenerating] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);

  // Helper: returns yellow-ring class when a field is highlighted by the AI chatbot
  const hl = (field) =>
    highlightedFields.has(field) ? ' ring-2 ring-yellow-400 ring-offset-1 transition-all' : '';

  // Apply actions dispatched by AiChatBot — stagger fields, highlight each briefly
  useEffect(() => {
    if (!pendingActions || pendingActions.length === 0) return;

    const SECTION_FIELDS = {
      campaign: ['name', 'publisherPlatform', 'objective', 'status'],
      adset: ['location', 'gender', 'age', 'languages', 'adSetName', 'startTime', 'endTime',
              'budgetMeta', 'budgetTikTok', 'budgetGoogle', 'budgetSnapchat', 'budgetLinkedIn', 'budgetTaboola'],
      ad: ['adCreativeName', 'creativeType', 'adTitle', 'adText', 'description', 'callToAction', 'destinationUrl'],
    };

  
    // Apply every field value immediately — formData must be correct as soon as
    // pendingActions changes, since a chat "_submit" can follow right behind
    // these actions (BUG-017 / CT-096). Previously this write was staggered
    // behind the same setTimeout as the highlight animation below, so a fast
    // "Confirm launch" click could fire handleSubmit before the last field's
    // value had actually landed in formData.
    setFormData((prev) => {
      const updated = { ...prev };
      pendingActions.forEach((action) => {
        updated[action.field] = action.value;
      });
      return updated;
    });

    // Stagger only the cosmetic cascade — section reveal + yellow highlight.
    pendingActions.forEach((action, idx) => {
      setTimeout(() => {
        // Open the relevant section
        if (SECTION_FIELDS.campaign.includes(action.field)) setShowCampaign(true);
        if (SECTION_FIELDS.adset.includes(action.field)) setShowAdset(true);
        if (SECTION_FIELDS.ad.includes(action.field)) setShowAd(true);

        // Yellow highlight for 1.5 s
        setHighlightedFields((prev) => new Set([...prev, action.field]));
        setTimeout(() => {
          setHighlightedFields((prev) => {
            const next = new Set(prev);
            next.delete(action.field);
            return next;
          });
        }, 1500);
      }, idx * 200);
    });

    clearActions();
  }, [pendingActions, clearActions]);

  // Triggered by AiChatBot when user confirms campaign launch
  useEffect(() => {
    if (submitSignal > 0) {
      handleSubmit({ preventDefault: () => {} });
    }
  }, [submitSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load real TikTok interest categories from the API when TikTok is selected
  useEffect(() => {
    if (formData.publisherPlatform.includes('TikTok')) {
      getRequest('/api/ads/tiktok/interest-categories')
        .then((res) => {
          if (res?.data?.length) {
            setTiktokInterestCategories(res.data.map((i) => ({ value: i.id, label: i.name })));
          }
        })
        .catch(() => {}); // silently fall back to static list
    }
  }, [formData.publisherPlatform]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { calculateTotalBudget(); }, [formData.budgetMeta, formData.budgetTikTok, formData.budgetGoogle, formData.budgetSnapchat, formData.budgetLinkedIn, formData.budgetShopify, formData.budgetTaboola]);

  // Check Shopify connection (non-blocking)
  useEffect(() => {
    getRequest('/api/ads/shopify-oauth/status')
      .then((res) => setShopifyConnected(!!res.connected))
      .catch(() => {});
  }, []);

  // Fetch connected platforms — platforms NOT connected are disabled in the selector by default
  useEffect(() => {
    getRequest('/api/connected-platforms/all')
      .then((data) => {
        const names = (Array.isArray(data) ? data : []).map((p) => (p.name || '').toLowerCase());
        setConnectedPlatforms(new Set(names));
      })
      .catch(() => {});
  }, []);

  // Wallet balance — used only for the non-blocking insufficient-funds warning
  // below (BUG-018 / CT-097). Meta, TikTok, Snapchat, and LinkedIn charge the
  // wallet atomically on submit and will 402 if the balance is too low; this
  // just surfaces that risk before the user fills out the whole form.
  useEffect(() => {
    getRequest('/api/auth/user-info')
      .then((u) => setWalletBalance(parseFloat(u?.wallet_balance || 0)))
      .catch(() => {});
  }, []);

  // Pre-fill from Shopify product (prop, location state, or modal selection)
  useEffect(() => {
    if (!effectiveProduct) return;
    setFormData((prev) => ({
      ...prev,
      name: effectiveProduct.title || prev.name,
      adCreativeName: effectiveProduct.title || prev.adCreativeName,
      adTitle: effectiveProduct.title || prev.adTitle,
      adText: effectiveProduct.description || prev.adText,
      description: effectiveProduct.description || prev.description,
      destinationUrl: effectiveProduct.product_url || prev.destinationUrl,
      imageUrl: effectiveProduct.image_url || prev.imageUrl,
      creativeType: effectiveProduct.image_url ? 'Image' : prev.creativeType,
      callToAction: prev.callToAction || 'SHOP_NOW',
      ...(initialPlatform ? { publisherPlatform: [initialPlatform] } : {}),
    }));
    setShowAd(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveProduct]);

  useEffect(() => {
    if (formData.publisherPlatform.includes('Facebook') || formData.publisherPlatform.includes('Instagram')) {
      fetchMetaAdAccounts();
    }
    if (formData.publisherPlatform.includes('TikTok')) {
      fetchTiktokAdAccounts();
    }
    if (formData.publisherPlatform.includes('LinkedIn')) {
      fetchLinkedInAdAccounts();
    }
    if (formData.publisherPlatform.includes('Snapchat')) {
      fetchSnapchatAdAccounts();
    }
    if (formData.publisherPlatform.includes('Google')) {
      fetchGoogleAdAccounts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.publisherPlatform]);

  useEffect(() => {
    fetchAudienceEstimate(formData, selectedAdAccount, setAudienceEstimate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.publisherPlatform, formData.location, formData.age, formData.gender, formData.languages, formData.interests, selectedAdAccount]);

  useEffect(() => {
    if (selectedAdAccount) fetchMetaBusinessData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAdAccount]);

  useEffect(() => {
    removeOldPromotedObjectFields();
    generatePromotedObjectFields(formData.objective, formData.publisherPlatform);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.objective, formData.publisherPlatform, metaData]);

  // "App Installs" (OUTCOME_APP_PROMOTION) is a Meta-only objective — TikTok can't
  // fulfil it in this web flow. Hide it whenever TikTok is selected (see the
  // objective Select below), and if it was already chosen, fall back to Traffic so
  // the campaign objective can never be a value TikTok silently reinterprets.
  useEffect(() => {
    setFormData((prev) =>
      prev.publisherPlatform.includes('TikTok') && prev.objective === 'OUTCOME_APP_PROMOTION'
        ? { ...prev, objective: 'OUTCOME_TRAFFIC' }
        : prev
    );
  }, [formData.publisherPlatform, formData.objective]);

  const removeOldPromotedObjectFields = () => {
    const updatedFormData = { ...formData };
    promotedObjectFields.forEach(field => { delete updatedFormData[field.name]; });
    setFormData(updatedFormData);
  };

  const calculateTotalBudget = () => {
    const total = (parseFloat(formData.budgetMeta) || 0) + (parseFloat(formData.budgetTikTok) || 0) + (parseFloat(formData.budgetGoogle) || 0) + (parseFloat(formData.budgetSnapchat) || 0) + (parseFloat(formData.budgetLinkedIn) || 0) + (parseFloat(formData.budgetShopify) || 0) + (parseFloat(formData.budgetTaboola) || 0);
    setFormData(prev => ({ ...prev, totalBudget: total, smartBudget: total }));
  };

  // ── FIXED: calls the real backend endpoint ──
  const fetchMetaAdAccounts = async () => {
    try {
      const response = await getRequest('/api/ads/meta/ad-accounts');
      if (response?.connected && response?.adAccounts?.length) {
        const accounts = response.adAccounts.map(acc => ({
          value: acc.adAccountId,
          label: `${acc.name} (${acc.adAccountId})`,
          accessToken: acc.accessToken,
        }));
        setMetaAdAccounts(accounts);
        // Auto-select the first account so reach estimate fires immediately
        if (accounts.length > 0) {
          const first = accounts[0];
          setSelectedAdAccount(prev => prev || first.value);
          setFormData(prev => ({ ...prev, adAccountMeta: prev.adAccountMeta || first.value }));
          if (first.accessToken) setSelectedAccessToken(t => t || first.accessToken);
        }
      } else {
        toast.error("No Meta ad accounts found. Make sure Meta is connected.");
      }
    } catch (error) {
      console.error("Error fetching Meta ad accounts:", error);
      toast.error("Failed to load Meta ad accounts. Is Meta connected?");
    }
  };

  const fetchLinkedInAdAccounts = async () => {
    try {
      const response = await getRequest('/api/ads/linkedin/ad-accounts');
      const accounts = response?.data?.accounts || [];
      if (accounts.length) {
        setLinkedInAdAccounts(accounts.map(acc => ({ value: acc.id, label: `${acc.name} (${acc.id})` })));
      } else {
        toast.error("No LinkedIn ad accounts found. Make sure LinkedIn is connected and an account is selected.");
      }
    } catch (error) {
      console.error("Error fetching LinkedIn ad accounts:", error);
      toast.error("Failed to load LinkedIn ad accounts. Is LinkedIn connected?");
    }
  };

  const fetchTiktokAdAccounts = async () => {
    try {
      const response = await getRequest('/api/ads/tiktok/fetch-tiktok-ads-account');
      const ids = response?.data?.advertiser_ids || [];
      if (ids.length) {
        const accounts = ids.map(id => ({ value: id, label: `Advertiser ${id}` }));
        setTiktokAdAccounts(accounts);
        // Auto-select first advertiser
        setFormData(prev => ({ ...prev, adAccountTikTok: prev.adAccountTikTok || accounts[0].value }));
      } else {
        toast.error("No TikTok advertiser IDs found. Make sure TikTok is connected and an advertiser is selected.");
      }
    } catch (error) {
      console.error("Error fetching TikTok ad accounts:", error);
      toast.error("Failed to load TikTok advertiser IDs. Is TikTok connected?");
    }
  };

  const fetchSnapchatAdAccounts = async () => {
    try {
      const response = await getRequest('/api/ads/snapchat/fetch-available-ad-accounts');
      const accounts = response?.data || [];
      if (accounts.length) {
        const mapped = accounts.map(acc => ({ value: acc.id, label: `${acc.name || acc.id} (${acc.id})` }));
        setSnapchatAdAccounts(mapped);
        setSelectedAdAccount(prev => prev || mapped[0].value);
        setFormData(prev => ({ ...prev, adAccountSnapchat: prev.adAccountSnapchat || mapped[0].value }));
      } else {
        toast.error("No Snapchat ad accounts found. Make sure Snapchat is connected.");
      }
    } catch (error) {
      console.error("Error fetching Snapchat ad accounts:", error);
      toast.error("Failed to load Snapchat ad accounts. Is Snapchat connected?");
    }
  };

  const fetchGoogleAdAccounts = async () => {
    try {
      const response = await getRequest('/api/ads/google/fetch-accessible-ads-accounts');
      const accounts = response?.data || [];
      if (accounts.length) {
        setGoogleAdAccounts(accounts.map(acc => ({ value: acc.id, label: `Account ${acc.id}` })));
      } else {
        toast.error("No Google Ads accounts found. Make sure Google is connected and an account is selected.");
      }
    } catch (error) {
      console.error("Error fetching Google ad accounts:", error);
      toast.error("Failed to load Google Ads accounts. Is Google connected?");
    }
  };

  const handleToggleChange = () => {
    if (!isPremium) { toast.error("Upgrade to Pro to access advanced campaign options."); return; }
    setIsSimpleCampaign(!isSimpleCampaign);
  };

  const handleToggleSection = (section) => {
    if (section === 'Campaign') setShowCampaign(!showCampaign);
    else if (section === 'Adset') setShowAdset(!showAdset);
    else if (section === 'Ad') setShowAd(!showAd);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (value?.toString().trim() !== '') {
      setErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const handleMultiSelectChange = (name, selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map(o => o.value) : [];
    setFormData({ ...formData, [name]: values });
    setErrors(prev => ({ ...prev, [name]: values.length === 0 }));
  };

  const handleSelectChange = (name, selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map(o => o.value) : [];
    setFormData({ ...formData, [name]: values });
  };

  const handleSelectPromtedObjectChange = (name, selectedOption) => {
    setFormData(prev => ({ ...prev, [name]: selectedOption ? selectedOption.value : '' }));
  };

  const handleBudgetChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    if (parseFloat(value) > 0) setErrors(prev => ({ ...prev, [name]: false }));
  };

  const handleAdAccountChange = (selectedOption) => { // eslint-disable-line no-unused-vars
    setFormData({ ...formData, adAccountMeta: selectedOption.value });
    setSelectedAccessToken(selectedOption.accessToken);
    setSelectedAdAccount(selectedOption.value);
    if (selectedOption?.value) setErrors(prev => ({ ...prev, adAccountMeta: false }));
  };

  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;
    const mediaType = type.toLowerCase();

    if (!isAllowedFile(file, mediaType)) {
      const allowed = ALLOWED_FILE_TYPES[mediaType]?.extensions.join(', ') || '';
      toast.error(`Unsupported file type. Allowed: ${allowed}`);
      event.target.value = '';
      return;
    }

    const uploadPreset = 'd5rr7y5s';
    const cloudName = 'dxtj7sllk';
    const fd = new FormData();
    fd.append('file', file);
    fd.append('upload_preset', uploadPreset);
    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/${mediaType === 'videothumbnail' ? 'image' : mediaType}/upload`,
        { method: 'POST', body: fd }
      );
      const data = await response.json();
      if (mediaType === 'image') setFormData(prev => ({ ...prev, imageUrl: data.secure_url }));
      else if (mediaType === 'video') setFormData(prev => ({ ...prev, videoUrl: data.secure_url }));
      else if (mediaType === 'videothumbnail') setFormData(prev => ({ ...prev, videoThumbnailUrl: data.secure_url }));
      setErrors(prev => ({
        ...prev,
        ...(mediaType === 'image' && { imageFile: false }),
        ...(mediaType === 'video' && { videoFile: false }),
        ...(mediaType === 'videothumbnail' && { videoThumbnailFile: false }),
      }));
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('File upload failed. Check Cloudinary config.');
    }
  };

  // ── FIXED: real campaign submission ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name) newErrors.name = true;
    if (!formData.callToAction) newErrors.callToAction = true;
    if (!formData.destinationUrl) newErrors.destinationUrl = true;
    if (!formData.publisherPlatform.length) newErrors.publisherPlatform = true;
    if (!formData.adSetName) newErrors.adSetName = true;
    if (!formData.startTime) newErrors.startTime = true;
    if (!formData.adCreativeName) newErrors.adCreativeName = true;
    if (formData.creativeType === 'Image' && !formData.imageUrl) newErrors.imageFile = true;
    if (formData.creativeType === 'Video' && !formData.videoUrl) newErrors.videoFile = true;
    if (!formData.adTitle) newErrors.adTitle = true;
    if (!formData.adText) newErrors.adText = true;
    if (!formData.description) newErrors.description = true;

    const platforms = formData.publisherPlatform.map(p => p.toLowerCase());
    if ((platforms.includes('facebook') || platforms.includes('instagram')) && !formData.budgetMeta) {
      newErrors.budgetMeta = true;
    }
    if ((platforms.includes('facebook') || platforms.includes('instagram')) && !formData.adAccountMeta) {
      newErrors.adAccountMeta = true;
    }
    if (platforms.includes('tiktok') && !formData.budgetTikTok) {
      newErrors.budgetTikTok = true;
    }
    if (platforms.includes('tiktok') && !formData.adAccountTikTok) {
      newErrors.adAccountTikTok = true;
    }
    if (platforms.includes('google') && !formData.budgetGoogle) {
      newErrors.budgetGoogle = true;
    }
    if (platforms.includes('google') && !formData.adAccountGoogle) {
      newErrors.adAccountGoogle = true;
    }
    if (platforms.includes('snapchat') && !formData.budgetSnapchat) {
      newErrors.budgetSnapchat = true;
    }
    if (platforms.includes('snapchat') && !formData.adAccountSnapchat) {
      newErrors.adAccountSnapchat = true;
    }
    if (platforms.includes('linkedin') && !formData.budgetLinkedIn) {
      newErrors.budgetLinkedIn = true;
    }
    if (platforms.includes('linkedin') && !formData.adAccountLinkedIn) {
      newErrors.adAccountLinkedIn = true;
    }
    if (platforms.includes('taboola') && !formData.budgetTaboola) {
      newErrors.budgetTaboola = true;
    }
    if (platforms.includes('taboola') && formData.bidStrategyTaboola === 'TARGET_CPA' && !formData.cpaGoalTaboola) {
      newErrors.cpaGoalTaboola = true;
    }
    if (platforms.includes('shopify') && !formData.budgetShopify) {
      newErrors.budgetShopify = true;
    }
    if (formData.creativeType === 'Video' && !formData.videoThumbnailUrl) {
      newErrors.videoThumbnailFile = true;
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error('Please fill all required fields.');
      return;
    }

    setSubmitting(true);

    // Build per-platform tasks — all run in parallel via Promise.allSettled
    // so a failure on one platform never blocks the others
    const PLATFORM_TASKS = [
      {
        key: 'meta',
        label: 'Meta',
        active: platforms.includes('facebook') || platforms.includes('instagram'),
        run: () => postRequest('/api/ads/meta/create-campaign', formData),
      },
      {
        key: 'tiktok',
        label: 'TikTok',
        active: platforms.includes('tiktok'),
        run: () => postRequest('/api/ads/tiktok/create-campaign', formData),
      },
      {
        key: 'google',
        label: 'Google',
        active: platforms.includes('google'),
        run: () => postRequest('/api/ads/google/create-campaign', formData),
      },
      {
        key: 'snapchat',
        label: 'Snapchat',
        active: platforms.includes('snapchat'),
        run: () => postRequest('/api/ads/snapchat/create-campaign', formData),
      },
      {
        key: 'linkedin',
        label: 'LinkedIn',
        active: platforms.includes('linkedin'),
        run: () => postRequest('/api/ads/linkedin/create-campaign', formData),
      },
      {
        key: 'taboola',
        label: 'Taboola',
        active: platforms.includes('taboola'),
        run: () => postRequest('/api/ads/taboola/create-campaign', formData),
      },
      {
        key: 'shopify',
        label: 'Shopify',
        active: platforms.includes('shopify'),
        run: () => postRequest('/api/ads/shopify-oauth/create-campaign', formData),
      },
    ];

    const active = PLATFORM_TASKS.filter(t => t.active);
    const settled = await Promise.allSettled(active.map(t => t.run()));

    const succeeded = [];
    const failed = [];

    settled.forEach((result, idx) => {
      const { label, key } = active[idx];
      if (result.status === 'fulfilled') {
        if (result.value?.info) {
          toast.info(result.value.info);
        } else {
          succeeded.push(label);
        }
      } else {
        const msg = result.reason?.response?.data?.message || result.reason?.message || 'Unknown error';
        failed.push({ label, msg });
        console.error(`[CreateAd] ${key} error:`, msg);
      }
    });

    if (succeeded.length > 0) {
      toast.success(`Campaign created on: ${succeeded.join(', ')}`);
      sessionStorage.removeItem(DRAFT_STORAGE_KEY);
    }
    failed.forEach(({ label, msg }) => {
      toast.error(`${label} failed: ${msg}`, { autoClose: 8000 });
    });

    setSubmitting(false);
  };

  const fetchMetaBusinessData = async () => {
    try {
      const businessResponse = await fetch(
        `https://graph.facebook.com/v24.0/me/businesses?access_token=${selectedAccessToken}`
      );
      const businessData = await businessResponse.json();
      const businessId = businessData.data?.[0]?.id;
      if (!businessId) return;

      const [pagesRes, appsRes, pixelsRes, igRes] = await Promise.all([
        fetch(`https://graph.facebook.com/v24.0/${businessId}/owned_pages?access_token=${selectedAccessToken}`).then(r => r.json()),
        fetch(`https://graph.facebook.com/v24.0/${businessId}/owned_apps?access_token=${selectedAccessToken}`).then(r => r.json()),
        fetch(`https://graph.facebook.com/v24.0/${businessId}/adspixels?access_token=${selectedAccessToken}`).then(r => r.json()),
        // Broken: Meta deprecated the Business `instagram_accounts` edge (removed v22.0+, effective Apr/May 2025) — needs migration to Instagram Platform auth, not just a version bump.
        fetch(`https://graph.facebook.com/v24.0/${businessId}/instagram_accounts?access_token=${selectedAccessToken}`).then(r => r.json()),
      ]);

      setMetaData({
        businessId,
        pages: pagesRes.data?.map(p => ({ value: p.id, label: `${p.name} (${p.id})` })) || [],
        apps: appsRes.data?.map(a => ({ value: a.id, label: `${a.name} (${a.id})` })) || [],
        pixels: pixelsRes.data?.map(p => ({ value: p.id, label: `${p.name} (${p.id})` })) || [],
        instagramProfiles: igRes.data?.map(i => ({ value: i.id, label: i.username || i.id })) || [],
      });
    } catch (error) {
      console.error('Error fetching Meta business data:', error);
    }
  };

  const generatePromotedObjectFields = (objective, platforms) => {
    let fields = [];
    const addField = (name, type, platform, options = []) => {
      if (!fields.some(f => f.name === name && f.platform === platform)) {
        fields.push({ name, type, platform, options });
        setFormData(prev => ({ ...prev, [name]: prev[name] || '' }));
      }
    };
    const isMeta = platforms.includes('Facebook') || platforms.includes('Instagram');
    const isIG = platforms.includes('Instagram');
    const isTT = platforms.includes('TikTok'); // eslint-disable-line no-unused-vars

    // page_id is always required by Meta API for AdCreative creation
    if (isMeta) addField('page_id', 'select', 'Meta', metaData.pages);

    switch (objective) {
      case 'OUTCOME_TRAFFIC':
        if (isMeta) addField('website_url', 'url', 'Meta');
        if (isIG) addField('instagram_actor_id', 'select', 'Meta', metaData.instagramProfiles);
        break;
      case 'OUTCOME_CONVERSIONS':
        if (isMeta) { addField('website_url', 'url', 'Meta'); addField('pixel_id', 'select', 'Meta', metaData.pixels); }
        break;
      case 'OUTCOME_APP_PROMOTION':
        if (isMeta) addField('app_id', 'select', 'Meta', metaData.apps);
        break;
      case 'OUTCOME_AWARENESS':
      case 'OUTCOME_REACH':
        if (isMeta) addField('profile_url', 'url', 'Meta');
        break;
      case 'OUTCOME_LEADS':
        if (isMeta) addField('lead_form', 'text', 'Meta');
        break;
      default: break;
    }
    setPromotedObjectFields(fields);
  };

  const selectStyles = {
    control: (p) => ({ ...p, backgroundColor: '#f3f4f6', borderColor: '#d1d5db' }),
    menu: (p) => ({ ...p, backgroundColor: '#f3f4f6' }),
  };

  const renderBudgetSection = (platform, budgetTypeName, budgetName, currencyName, adAccountName, adAccounts) => (
    <div className="grid grid-cols-2 gap-4 mt-4 col-span-2">
      <div className="flex space-x-2 w-full">
        <div className="w-1/3">
          <label className="block text-sm font-semibold text-[#221C51]">Budget Type</label>
          <select name={budgetTypeName} value={formData[budgetTypeName] || 'Daily'} onChange={handleInputChange}
            className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100">
            <option value="Daily">Daily</option>
            <option value="Lifetime">Lifetime</option>
          </select>
        </div>
        <div className="w-1/3">
          <label className="block text-sm font-semibold text-[#221C51]">Budget {platform} <span className="text-red-500">*</span></label>
          <input type="number" name={budgetName} placeholder="Enter Budget" value={formData[budgetName] || ''}
            onChange={(e) => handleBudgetChange(budgetName, e.target.value)}
            className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
          {errors[budgetName] && <span className="text-red-500 text-xs">Budget {platform} is required</span>}
        </div>
        <div className="w-1/3">
          <label className="block text-sm font-semibold text-[#221C51]">Currency</label>
          <select name={currencyName} value={formData[currencyName] || 'MAD'} onChange={handleInputChange}
            className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100">
            <option value="MAD">MAD</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
          </select>
        </div>
      </div>
      <div className="w-full">
        <label className="block text-sm font-semibold text-[#221C51]">{platform} Ad Account <span className="text-red-500">*</span></label>
        <Select
          name={adAccountName}
          options={adAccounts}
          onChange={(sel) => {
            if (!sel) return;
            setFormData(prev => ({ ...prev, [adAccountName]: sel.value }));
            if (sel.accessToken) setSelectedAccessToken(sel.accessToken);
            if (adAccountName === 'adAccountMeta') setSelectedAdAccount(sel.value);
            setErrors(prev => ({ ...prev, [adAccountName]: false }));
          }}
          className="text-sm"
          styles={selectStyles}
        />
        {errors[adAccountName] && <span className="text-red-500 text-xs">Ad account is required</span>}
      </div>
    </div>
  );

  const platformOptions = PLATFORMS.map((p) => ({ value: p.value, label: p.label, icon: <p.Icon /> }));

  // A platform option is disabled by default until its account is connected.
  // Uses the shared PLATFORM mapping so Shopify counts as connected via EITHER
  // 'shopify' or 'shopify-oauth' (BUG-001 / CT-041).
  const isPlatformDisabled = (opt) => !isChannelConnected(opt.value, connectedPlatforms);

  const objectiveOptions = [
    { value: 'OUTCOME_TRAFFIC', label: 'Traffic', icon: <FaRoad /> },
    { value: 'OUTCOME_ENGAGEMENT', label: 'Engagement', icon: <FaThumbsUp /> },
    { value: 'OUTCOME_APP_PROMOTION', label: 'App Installs', icon: <FaMobileAlt /> },
    { value: 'OUTCOME_AWARENESS', label: 'Awareness / Reach', icon: <FaBroadcastTower /> },
    { value: 'OUTCOME_LEADS', label: 'Lead Generation', icon: <FaClipboardList /> },
    { value: 'OUTCOME_CONVERSIONS', label: 'Conversions', icon: <FaShoppingCart /> },
  ];

  // App Installs is only offered for Meta; TikTok has no web-compatible app
  // objective, so drop it from the choices whenever TikTok is selected.
  const visibleObjectiveOptions = formData.publisherPlatform.includes('TikTok')
    ? objectiveOptions.filter((o) => o.value !== 'OUTCOME_APP_PROMOTION')
    : objectiveOptions;

  const categoryOptions = [
    { value: 'CREDIT', label: 'Credit', icon: <FaCreditCard /> },
    { value: 'EMPLOYMENT', label: 'Employment', icon: <FaBriefcase /> },
    { value: 'HOUSING', label: 'Housing', icon: <FaHome /> },
    { value: 'ISSUES_ELECTIONS_POLITICS', label: 'Social / Political', icon: <FaBullhorn /> },
  ];

  const bidStrategyOptions = [
    { value: 'LOWEST_COST', label: 'Lowest Cost (Automatic)' },
    { value: 'COST_CAP', label: 'Cost Cap' },
    { value: 'BID_CAP', label: 'Bid Cap' },
    { value: 'TARGET_COST', label: 'Target Cost' },
  ];

  const taboolaBidStrategyOptions = [
    { value: 'FIXED',           label: 'Fixed CPC' },
    { value: 'SMART',           label: 'Smart (Enhanced CPC)' },
    { value: 'MAX_CONVERSIONS', label: 'Max Conversions (Auto)' },
    { value: 'TARGET_CPA',      label: 'Target CPA' },
  ];

  const taboolaMarketingObjectiveOptions = [
    { value: 'DRIVE_WEBSITE_TRAFFIC',          label: 'Drive Website Traffic' },
    { value: 'LEADS_GENERATION',               label: 'Leads Generation' },
    { value: 'ONLINE_PURCHASES',               label: 'Online Purchases' },
    { value: 'BRAND_AWARENESS_AND_ENGAGEMENT', label: 'Brand Awareness & Engagement' },
    { value: 'APP_INSTALLS',                   label: 'App Installs' },
  ];

  const handleGenerateCreative = async (type) => {
    if (type !== 'animate' && !aiPrompt.trim()) {
      toast.error('Enter a prompt to generate the creative.');
      return;
    }
    if (type === 'animate' && !formData.imageUrl) {
      toast.error('No source image available for animation.');
      return;
    }
    setGenerating(true);
    try {
      const res = await postRequest('/api/creative/generate', {
        type,
        prompt: aiPrompt,
        aspectRatio: aiAspectRatio,
        ...(type === 'animate' && { imageUrl: formData.imageUrl }),
      });
      if (type === 'image') setFormData(p => ({ ...p, imageUrl: res.url }));
      if (type === 'video' || type === 'animate') setFormData(p => ({ ...p, videoUrl: res.url }));
      toast.success('Creative generated successfully!');
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || 'Failed to generate creative.');
    } finally {
      setGenerating(false);
    }
  };

  // Non-blocking warning: wallet-billed platforms only (Google/Taboola/Shopify
  // aren't wallet-gated server-side — see walletService.chargeWalletForCampaign).
  const WALLET_GATED_BUDGET_FIELD = { Facebook: 'budgetMeta', Instagram: 'budgetMeta', TikTok: 'budgetTikTok', Snapchat: 'budgetSnapchat', LinkedIn: 'budgetLinkedIn' };
  let insufficientWalletWarning = null;
  if (walletBalance != null) {
    const activeFields = new Set(formData.publisherPlatform.map((p) => WALLET_GATED_BUDGET_FIELD[p]).filter(Boolean));
    const totalGatedBudget = [...activeFields].reduce((sum, field) => sum + (parseFloat(formData[field]) || 0), 0);
    if (totalGatedBudget > 0 && totalGatedBudget > walletBalance) {
      insufficientWalletWarning = `Your wallet balance (€${walletBalance.toFixed(2)}) is below the budget entered for wallet-billed platforms (€${totalGatedBudget.toFixed(2)}). Meta, TikTok, Snapchat, and LinkedIn are charged from your wallet — top up in Billing, or publishing will fail for those platforms.`;
    }
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      {effectiveProduct && (
        <div className="flex items-center gap-3 mb-4 px-4 py-3 bg-green-50 border border-green-200 rounded-xl">
          {effectiveProduct.image_url && (
            <img src={effectiveProduct.image_url} alt={effectiveProduct.title} className="w-10 h-10 object-cover rounded-lg flex-shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-xs font-semibold text-green-800 leading-tight truncate">
              <FaShopify className="inline mr-1" style={{ color: '#96bf48' }} />
              Pre-filled from Shopify: {effectiveProduct.title}
            </p>
            <p className="text-xs text-green-600 mt-0.5">Title, text, image, and URL pre-filled. Review and adjust as needed.</p>
            {aiMode && effectiveProduct.image_url && (
              <button
                type="button"
                onClick={() => handleGenerateCreative('animate')}
                disabled={generating}
                className="mt-1.5 flex items-center gap-1.5 px-3 py-1 bg-purple-600 text-white text-xs font-semibold rounded-md hover:bg-purple-700 disabled:opacity-50 transition"
              >
                <FaMagic /> {generating ? 'Animating...' : 'Animate this product (AI)'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Shopify import modal */}
      {shopifyModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center pt-16 px-4"
          onClick={() => setShopifyModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-[#221C51] text-lg flex items-center gap-2">
                <FaShopify style={{ color: '#96bf48' }} /> Import from Shopify
              </h3>
              <button
                onClick={() => setShopifyModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <ShopifyProductPicker
              selected={localShopifyProduct}
              onSelect={(product) => { setLocalShopifyProduct(product); setShopifyModalOpen(false); }}
            />
          </div>
        </div>
      )}
      <div className="flex justify-between items-center w-full">
        <div>
          <h2 className="text-2xl font-bold text-[#221C51]">{aiMode ? 'Create Your Ad with AI' : "Let's Create Your Ad"}</h2>
          <p className="text-xs text-gray-500 mt-0.5">{aiMode ? 'The assistant fills the form as you chat.' : 'Configure your campaign across connected platforms.'}</p>
        </div>
        <div className="flex items-center gap-3">
          {shopifyConnected && !effectiveProduct && (
            <button
              type="button"
              onClick={() => setShopifyModalOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 border border-[#96bf48] text-[#5a7a2a] text-xs font-semibold rounded-xl hover:bg-[#96bf48]/10 transition-colors"
            >
              <FaShopify style={{ color: '#96bf48' }} />
              Import from Shopify
            </button>
          )}
          <div className="flex items-center bg-gray-100 rounded-full p-0.5 text-xs font-semibold">
            <button
              type="button"
              onClick={() => onSwitchToManual ? onSwitchToManual() : navigate('/dashboard/create-ad')}
              className={`px-3 py-1 rounded-full transition-all duration-200 ${!aiMode ? 'bg-[#221C51] text-white shadow' : 'text-gray-500 hover:text-[#221C51]'}`}
            >
              Manual
            </button>
            <button
              type="button"
              onClick={() => onSwitchToAI ? onSwitchToAI() : navigate('/dashboard/create-ad-ai')}
              className={`px-3 py-1 rounded-full transition-all duration-200 ${aiMode ? 'bg-[#221C51] text-white shadow' : 'text-gray-500 hover:text-[#221C51]'}`}
            >
              Use AI
            </button>
          </div>
          <p className="text-sm text-[#221C51] mr-2 font-semibold">{isSimpleCampaign ? 'Simple' : 'Advanced'}</p>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={!isSimpleCampaign} onChange={handleToggleChange} className="sr-only peer" />
            <div className="w-12 h-6 bg-[#221C51] rounded-full">
              <div className="w-6 h-6 bg-white rounded-full shadow-md transform peer-checked:translate-x-6 transition-transform duration-200"></div>
            </div>
          </label>
        </div>
      </div>
      <hr className="border-t border-[#221C51]/30 mt-3" />

      <div className="max-w-4xl mx-auto mt-4">
        <form className="grid grid-cols-2 gap-x-4 gap-y-4">

          {/* ── CAMPAIGN SECTION ── */}
          <div className="col-span-2">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-[#221C51]">Campaign</h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={showCampaign} onChange={() => handleToggleSection('Campaign')} className="sr-only peer" />
                <div className="w-10 h-5 bg-gray-200 rounded-full peer-checked:bg-[#221C51]">
                  <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${showCampaign ? 'translate-x-5' : 'translate-x-0'}`}></div>
                </div>
              </label>
            </div>

            {showCampaign && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-4 mt-2">
                <div>
                  <label className="block text-sm font-semibold text-[#221C51]">Campaign name <span className="text-red-500">*</span></label>
                  <input type="text" name="name" placeholder="Enter Campaign Name" value={formData.name} onChange={handleInputChange}
                    className={`block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100${hl('name')}`} />
                  {errors.name && <span className="text-red-500 text-xs">Name is required</span>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#221C51]">Campaign Objective</label>
                  <Select name="objective" value={visibleObjectiveOptions.find(o => o.value === formData.objective)}
                    onChange={(sel) => handleInputChange({ target: { name: 'objective', value: sel.value } })}
                    options={visibleObjectiveOptions} getOptionLabel={o => <div className="flex items-center">{o.icon}<span className="ml-2">{o.label}</span></div>}
                    className="text-sm" styles={selectStyles} />
                </div>

                {!isSimpleCampaign && (
                  <div>
                    <label className="block text-sm font-semibold text-[#221C51]">Category</label>
                    <Select name="category" options={categoryOptions} isMulti
                      value={categoryOptions.filter(o => formData.category.includes(o.value))}
                      onChange={(sel) => handleMultiSelectChange('category', sel)}
                      getOptionLabel={o => <div className="flex items-center">{o.icon}<span className="ml-2">{o.label}</span></div>}
                      className="text-sm" styles={selectStyles} />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-[#221C51]">Publisher Platform <span className="text-red-500">*</span></label>
                  <Select name="publisherPlatform" options={platformOptions} isMulti
                    value={platformOptions.filter(o => formData.publisherPlatform.includes(o.value))}
                    onChange={(sel) => handleMultiSelectChange('publisherPlatform', sel)}
                    isOptionDisabled={isPlatformDisabled}
                    getOptionLabel={o => (
                      <div className={`flex items-center ${isPlatformDisabled(o) ? 'opacity-40' : ''}`}>
                        {o.icon}<span className="ml-2">{o.label}</span>
                        {isPlatformDisabled(o) && <span className="ml-2 text-[10px] text-gray-400">(not connected)</span>}
                      </div>
                    )}
                    className="text-sm" styles={selectStyles} />
                  {errors.publisherPlatform && <span className="text-red-500 text-xs">Platform is required</span>}
                  <p className="text-[11px] text-gray-400 mt-1">Only connected platforms can be selected. Connect more in Platforms.</p>
                </div>

                {(formData.publisherPlatform.includes('Facebook') || formData.publisherPlatform.includes('Instagram')) &&
                  renderBudgetSection('Meta', 'budgetTypeMeta', 'budgetMeta', 'currencyMeta', 'adAccountMeta', metaAdAccounts)}
                {formData.publisherPlatform.includes('TikTok') &&
                  renderBudgetSection('TikTok', 'budgetTypeTikTok', 'budgetTikTok', 'currencyTikTok', 'adAccountTikTok', tiktokAdAccounts)}
                {formData.publisherPlatform.includes('Google') &&
                  renderBudgetSection('Google', 'budgetTypeGoogle', 'budgetGoogle', 'currencyGoogle', 'adAccountGoogle', googleAdAccounts)}
                {formData.publisherPlatform.includes('Snapchat') &&
                  renderBudgetSection('Snapchat', 'budgetTypeSnapchat', 'budgetSnapchat', 'currencySnapchat', 'adAccountSnapchat', snapchatAdAccounts)}
                {formData.publisherPlatform.includes('LinkedIn') && (
                  <>
                    {renderBudgetSection('LinkedIn', 'budgetTypeLinkedIn', 'budgetLinkedIn', 'currencyLinkedIn', 'adAccountLinkedIn', linkedInAdAccounts)}
                    <div className="col-span-2 mt-2">
                      <label className="block text-sm font-semibold text-[#221C51]">LinkedIn Bid / Cost Type</label>
                      <select
                        name="costTypeLinkedIn"
                        value={formData.costTypeLinkedIn}
                        onChange={handleInputChange}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:border-[#221C51]"
                      >
                        <option value="CPM">CPM — cost per 1,000 impressions</option>
                        <option value="CPC">CPC — cost per click</option>
                      </select>
                      <p className="text-xs text-gray-400 mt-1">LinkedIn auto-bids for the selected cost type. CPV (cost per view) requires a video creative.</p>
                    </div>
                  </>
                )}
                {formData.publisherPlatform.includes('Taboola') && (
                  <div className="col-span-2 mt-4 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#221C51]">Bid Strategy</label>
                      <select name="bidStrategyTaboola" value={formData.bidStrategyTaboola || 'FIXED'} onChange={handleInputChange}
                        className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100">
                        {taboolaBidStrategyOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#221C51]">Marketing Objective</label>
                      <select name="marketingObjectiveTaboola" value={formData.marketingObjectiveTaboola || 'DRIVE_WEBSITE_TRAFFIC'} onChange={handleInputChange}
                        className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100">
                        {taboolaMarketingObjectiveOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="flex space-x-2 w-full col-span-2">
                      <div className="w-1/3">
                        <label className="block text-sm font-semibold text-[#221C51]">Budget Type</label>
                        <select name="budgetTypeTaboola" value={formData.budgetTypeTaboola || 'Daily'} onChange={handleInputChange}
                          className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100">
                          <option value="Daily">Daily</option>
                          <option value="Lifetime">Lifetime</option>
                        </select>
                      </div>
                      <div className="w-1/3">
                        <label className="block text-sm font-semibold text-[#221C51]">Budget <span className="text-red-500">*</span></label>
                        <input type="number" name="budgetTaboola" placeholder="Enter Budget" value={formData.budgetTaboola || ''}
                          onChange={(e) => handleBudgetChange('budgetTaboola', e.target.value)}
                          className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                        {errors.budgetTaboola && <span className="text-red-500 text-xs">Budget is required</span>}
                      </div>
                      {['FIXED', 'SMART'].includes(formData.bidStrategyTaboola || 'FIXED') && (
                        <div className="w-1/3">
                          <label className="block text-sm font-semibold text-[#221C51]">CPC Bid</label>
                          <input type="number" step="0.01" name="cpcTaboola" placeholder="0.50"
                            value={formData.cpcTaboola || ''}
                            onChange={handleInputChange}
                            className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                        </div>
                      )}
                      {formData.bidStrategyTaboola === 'TARGET_CPA' && (
                        <div className="w-1/3">
                          <label className="block text-sm font-semibold text-[#221C51]">CPA Goal <span className="text-red-500">*</span></label>
                          <input type="number" step="0.01" name="cpaGoalTaboola" placeholder="e.g. 5.00"
                            value={formData.cpaGoalTaboola || ''}
                            onChange={handleInputChange}
                            className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                          {errors.cpaGoalTaboola && <span className="text-red-500 text-xs">CPA goal is required</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-700 col-span-2">
                      Taboola account linked via credentials. No ad account selection needed.
                    </div>
                  </div>
                )}
                {formData.publisherPlatform.includes('Shopify') && (
                  <div className="grid grid-cols-2 gap-4 col-span-2 mt-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#221C51]">Marketing Channel</label>
                      <select name="shopifyMarketingChannel" value={formData.shopifyMarketingChannel || 'social'} onChange={handleInputChange}
                        className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100">
                        <option value="social">Social</option>
                        <option value="search">Search</option>
                        <option value="display">Display</option>
                        <option value="email">Email</option>
                        <option value="referral">Referral</option>
                      </select>
                    </div>
                    <div className="flex space-x-2">
                      <div className="w-2/3">
                        <label className="block text-sm font-semibold text-[#221C51]">Budget <span className="text-red-500">*</span></label>
                        <input type="number" name="budgetShopify" placeholder="Enter Budget" value={formData.budgetShopify || ''}
                          onChange={(e) => handleBudgetChange('budgetShopify', e.target.value)}
                          className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                        {errors.budgetShopify && <span className="text-red-500 text-xs">Budget is required</span>}
                      </div>
                      <div className="w-1/3">
                        <label className="block text-sm font-semibold text-[#221C51]">Currency</label>
                        <select name="currencyShopify" value={formData.currencyShopify || 'MAD'} onChange={handleInputChange}
                          className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100">
                          <option value="MAD">MAD</option>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex items-center px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-700 col-span-2">
                      Creates a Shopify Marketing Event for your connected store. Tracked via UTM (source: onebotads).
                    </div>
                  </div>
                )}

                {formData.publisherPlatform.length > 0 && (
                  <div className="col-span-2 mt-2 flex items-center justify-end gap-3 rounded-xl bg-[#221C51]/5 border border-[#221C51]/10 px-4 py-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-[#221C51]">Total Budget</span>
                    <span className="text-lg font-black text-[#221C51]">{Number(formData.totalBudget || 0).toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── ADSET SECTION ── */}
          <div className="col-span-2 mt-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-[#221C51]">Adset</h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={showAdset} onChange={() => handleToggleSection('Adset')} className="sr-only peer" />
                <div className="w-10 h-5 bg-gray-200 rounded-full peer-checked:bg-[#221C51]">
                  <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${showAdset ? 'translate-x-5' : 'translate-x-0'}`}></div>
                </div>
              </label>
            </div>

            {showAdset && (
              <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-4">

                {/* ── Audience Estimate (Meta only) — sticky at top ── */}
                {(formData.publisherPlatform.includes('Facebook') || formData.publisherPlatform.includes('Instagram')) && (() => {
                  const fmtN = (n) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${Math.round(n / 1_000)}K` : String(n || 0);
                  const upper = audienceEstimate?.upperBound || 0;
                  const pct = upper > 100_000_000 ? 100 : upper > 50_000_000 ? 85 : upper > 10_000_000 ? 65 : upper > 2_000_000 ? 45 : upper > 500_000 ? 25 : 10;
                  const clr = upper > 50_000_000 ? 'bg-blue-400' : upper > 10_000_000 ? 'bg-green-500' : upper > 2_000_000 ? 'bg-green-400' : upper > 500_000 ? 'bg-orange-400' : 'bg-red-400';
                  const lbl = upper > 50_000_000 ? 'Very broad — ideal for awareness' : upper > 10_000_000 ? 'Broad — good for reach' : upper > 2_000_000 ? 'Balanced — good targeting' : upper > 500_000 ? 'Narrow — broaden your targeting' : 'Too narrow — adjust your criteria';
                  return (
                    <div className="col-span-2">
                      <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <FaBullseye className="text-[#221C51]" />
                            <span className="text-sm font-bold text-[#221C51] uppercase tracking-wide">Estimated Audience — Meta</span>
                          </div>
                          <span className="text-xs text-gray-400">Updated live</span>
                        </div>
                        {!selectedAdAccount ? (
                          <p className="text-xs text-gray-500">Select a Meta ad account to enable audience estimation.</p>
                        ) : audienceEstimate ? (
                          <>
                            <p className="text-3xl font-black text-[#221C51] leading-none">
                              {fmtN(audienceEstimate.lowerBound)} – {fmtN(audienceEstimate.upperBound)}
                              <span className="text-sm font-normal text-gray-500 ml-2">people</span>
                            </p>
                            <div className="mt-3 h-2 rounded-full bg-gray-200 overflow-hidden">
                              <div className={`h-full rounded-full transition-all duration-500 ${clr}`} style={{ width: `${pct}%` }} />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{lbl}</p>
                          </>
                        ) : (
                          <p className="text-xs text-gray-500">Set a country, age and gender to see the estimate.</p>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* ── Identity ── */}
                <div className="col-span-2">
                  <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Identity</h4>
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-semibold text-[#221C51]">Ad Set Name <span className="text-red-500">*</span></label>
                  <input type="text" name="adSetName" placeholder="Enter Ad Set Name" value={formData.adSetName || ''} onChange={handleInputChange}
                    className={`block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100${hl('adSetName')}`} />
                  {errors.adSetName && <span className="text-red-500 text-xs">Ad set name is required</span>}
                </div>

                <div className="col-span-1">
                  <label className="block text-sm font-semibold text-[#221C51]">Billing Event</label>
                  <select name="billingEvent" value={formData.billingEvent || ''} onChange={handleInputChange}
                    className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100">
                    <option value="IMPRESSIONS">Impressions</option>
                    <option value="LINK_CLICKS">Link Clicks</option>
                    <option value="APP_INSTALLS">App Installs</option>
                    <option value="THRUPLAY">ThruPlay</option>
                  </select>
                </div>

                <div className="col-span-1">
                  <label className="block text-sm font-semibold text-[#221C51]">Optimization Goal</label>
                  <select name="optimization_goal" value={formData.optimization_goal || ''} onChange={handleInputChange}
                    className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100">
                    <option value="REACH">Reach</option>
                    <option value="LINK_CLICKS">Link Clicks</option>
                    <option value="IMPRESSIONS">Impressions</option>
                    <option value="LEAD_GENERATION">Lead Generation</option>
                    <option value="APP_INSTALLS">App Installs</option>
                    <option value="OFFSITE_CONVERSIONS">Offsite Conversions</option>
                    <option value="THRUPLAY">ThruPlay</option>
                  </select>
                </div>

                {formData.publisherPlatform.length > 0 && !isSimpleCampaign && (
                  <div className="col-span-1">
                    <label className="block text-sm font-semibold text-[#221C51]">Bid Strategy</label>
                    <Select name="bid_strategy"
                      value={bidStrategyOptions.find(o => o.value === formData.bid_strategy)}
                      onChange={(sel) => handleInputChange({ target: { name: 'bid_strategy', value: sel.value } })}
                      options={bidStrategyOptions} styles={selectStyles} className="text-sm" />
                  </div>
                )}

                {/* ── Geography ── */}
                <div className="col-span-2 mt-2">
                  <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Geography</h4>
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-semibold text-[#221C51]">Country</label>
                  <Select name="location" isMulti
                    options={locationOptions}
                    value={locationOptions.flatMap(g => g.options).filter(o => formData.location?.includes(o.value))}
                    onChange={(sel) => {
                      handleSelectChange('location', sel);
                      if (!sel?.some(o => o.value === 'MA')) {
                        setFormData(prev => ({ ...prev, cities: [] }));
                      }
                    }}
                    placeholder="Select countries..."
                    className="text-sm" styles={selectStyles} />
                </div>

                {formData.location?.includes('MA') && (
                  <div className="col-span-1">
                    <label className="block text-sm font-semibold text-[#221C51]">
                      Morocco Cities <span className="text-xs font-normal text-gray-400">(optional)</span>
                    </label>
                    <Select name="cities" isMulti
                      options={moroccoCitiesOptions}
                      value={moroccoCitiesOptions.filter(o => formData.cities?.includes(o.value))}
                      onChange={(sel) => handleSelectChange('cities', sel)}
                      placeholder="All of Morocco..."
                      className="text-sm" styles={selectStyles} />
                  </div>
                )}

                {/* ── Demographics ── */}
                <div className="col-span-2 mt-2">
                  <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Demographics</h4>
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-semibold text-[#221C51]">Age</label>
                  <Select name="age" isMulti
                    options={[
                      { value: '18-24', label: '18-24' }, { value: '25-34', label: '25-34' },
                      { value: '35-44', label: '35-44' }, { value: '45-54', label: '45-54' },
                      { value: '55-65', label: '55-65' }, { value: 'all', label: 'All' },
                    ]}
                    value={formData.age?.map(a => ({ value: a, label: a }))}
                    onChange={(sel) => handleSelectChange('age', sel)}
                    className="text-sm" styles={selectStyles} />
                </div>

                <div className="col-span-1">
                  <label className="block text-sm font-semibold text-[#221C51]">Gender</label>
                  <div className="flex border border-[#d1d5db] rounded-md overflow-hidden">
                    {['All', 'Male', 'Female'].map(g => (
                      <button key={g} type="button"
                        className={`flex-1 py-2 text-sm font-medium ${formData.gender === g ? 'bg-[#221C51] text-white' : 'text-[#221C51] bg-white'} border-r border-[#d1d5db] last:border-none`}
                        onClick={() => setFormData({ ...formData, gender: g })}>{g}</button>
                    ))}
                  </div>
                </div>

                {!isSimpleCampaign && (
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-[#221C51]">
                      Languages <span className="text-xs font-normal text-gray-400">(optional — leave empty for all)</span>
                    </label>
                    <Select name="languages" isMulti
                      options={languageOptions}
                      value={languageOptions.filter(o => formData.languages?.includes(o.value))}
                      onChange={(sel) => handleSelectChange('languages', sel)}
                      placeholder="All languages..."
                      className="text-sm" styles={selectStyles} />
                  </div>
                )}

                {/* ── Interests & Placements section header ── */}
                {!isSimpleCampaign && (formData.publisherPlatform.includes('Facebook') || formData.publisherPlatform.includes('Instagram') || formData.publisherPlatform.includes('TikTok') || formData.publisherPlatform.includes('LinkedIn')) && (
                  <div className="col-span-2 mt-2">
                    <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Interests &amp; Placements</h4>
                  </div>
                )}

                {/* ── Interests ─────────────────────────────────────── */}
                {!isSimpleCampaign && (formData.publisherPlatform.includes('Facebook') || formData.publisherPlatform.includes('Instagram')) && (
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-[#221C51] mb-1">
                      <FaHeart className="inline mr-1 text-pink-500" />
                      Interests <span className="text-xs font-normal text-gray-400">(Meta — type to search)</span>
                    </label>
                    <AsyncSelect
                      isMulti
                      cacheOptions
                      loadOptions={loadMetaInterests}
                      value={(formData.interests || []).map(i => ({ value: i.id, label: i.name, id: i.id, name: i.name }))}
                      onChange={(sel) => setFormData(prev => ({
                        ...prev,
                        interests: (sel || []).map(o => ({ id: o.id || o.value, name: o.name || o.label })),
                      }))}
                      placeholder="Search interests (e.g. Football, Travel, Fashion)..."
                      noOptionsMessage={({ inputValue }) => inputValue.length < 2 ? 'Type at least 2 characters...' : 'No interests found'}
                      className="text-sm"
                      styles={selectStyles}
                    />
                  </div>
                )}

                {!isSimpleCampaign && formData.publisherPlatform.includes('TikTok') && (
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-[#221C51] mb-1">
                      <FaHeart className="inline mr-1 text-pink-500" />
                      Interests <span className="text-xs font-normal text-gray-400">(TikTok — select categories)</span>
                    </label>
                    <Select
                      isMulti
                      options={tiktokInterestCategories}
                      value={tiktokInterestCategories.filter(o => (formData.interests || []).some(i => i.id === o.value))}
                      onChange={(sel) => setFormData(prev => ({
                        ...prev,
                        interests: (sel || []).map(o => ({ id: o.value, name: o.label })),
                      }))}
                      placeholder="Select TikTok interest categories..."
                      className="text-sm"
                      styles={selectStyles}
                    />
                  </div>
                )}

                {/* ── TikTok Placement ─────────────────────────────── */}
                {!isSimpleCampaign && formData.publisherPlatform.includes('TikTok') && (
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-[#221C51] mb-1">TikTok Placement</label>
                    <Select name="tiktokPlacement"
                      value={[
                        { value: 'PLACEMENT_TYPE_AUTOMATIC', label: 'Automatic (Recommended)' },
                        { value: 'PLACEMENT_TYPE_NORMAL', label: 'Manual — TikTok In-Feed' },
                      ].find(o => o.value === (formData.tiktokPlacement || 'PLACEMENT_TYPE_AUTOMATIC'))}
                      onChange={(sel) => handleInputChange({ target: { name: 'tiktokPlacement', value: sel.value } })}
                      options={[
                        { value: 'PLACEMENT_TYPE_AUTOMATIC', label: 'Automatic (Recommended)' },
                        { value: 'PLACEMENT_TYPE_NORMAL', label: 'Manual — TikTok In-Feed' },
                      ]} styles={selectStyles} className="text-sm" />
                  </div>
                )}

                {/* ── Meta Placements ─────────────────────────────── */}
                {!isSimpleCampaign && (formData.publisherPlatform.includes('Facebook') || formData.publisherPlatform.includes('Instagram')) && (
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-[#221C51] mb-1">
                      Placements <span className="text-xs font-normal text-gray-400">(leave empty for Automatic Placements)</span>
                    </label>
                    <Select
                      isMulti
                      options={metaPlacementOptions}
                      value={metaPlacementOptions.flatMap(g => g.options).filter(o => (formData.placements || []).includes(o.value))}
                      onChange={(sel) => setFormData(prev => ({ ...prev, placements: (sel || []).map(o => o.value) }))}
                      placeholder="Automatic Placements (recommended)..."
                      className="text-sm"
                      styles={selectStyles}
                    />
                  </div>
                )}

                {/* ── LinkedIn-specific targeting ──────────────────── */}
                {!isSimpleCampaign && formData.publisherPlatform.includes('LinkedIn') && (
                  <div className="col-span-2 border border-blue-200 rounded-xl p-4 bg-blue-50">
                    <h4 className="text-sm font-bold text-[#221C51] mb-3 flex items-center gap-2">
                      <FaLinkedin className="text-[#0077b5]" /> LinkedIn Professional Targeting
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#221C51] mb-1">Seniority</label>
                        <Select isMulti options={linkedinSeniorityOptions}
                          value={linkedinSeniorityOptions.filter(o => (formData.linkedinSeniorities || []).includes(o.value))}
                          onChange={(sel) => setFormData(prev => ({ ...prev, linkedinSeniorities: (sel || []).map(o => o.value) }))}
                          placeholder="All seniorities..." className="text-sm" styles={selectStyles} />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#221C51] mb-1">Industry</label>
                        <Select isMulti options={linkedinIndustryOptions}
                          value={linkedinIndustryOptions.filter(o => (formData.linkedinIndustries || []).includes(o.value))}
                          onChange={(sel) => setFormData(prev => ({ ...prev, linkedinIndustries: (sel || []).map(o => o.value) }))}
                          placeholder="All industries..." className="text-sm" styles={selectStyles} />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#221C51] mb-1">Job Function</label>
                        <Select isMulti options={linkedinJobFunctionOptions}
                          value={linkedinJobFunctionOptions.filter(o => (formData.linkedinJobFunctions || []).includes(o.value))}
                          onChange={(sel) => setFormData(prev => ({ ...prev, linkedinJobFunctions: (sel || []).map(o => o.value) }))}
                          placeholder="All job functions..." className="text-sm" styles={selectStyles} />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#221C51] mb-1">Company Size</label>
                        <Select isMulti options={linkedinCompanySizeOptions}
                          value={linkedinCompanySizeOptions.filter(o => (formData.linkedinCompanySizes || []).includes(o.value))}
                          onChange={(sel) => setFormData(prev => ({ ...prev, linkedinCompanySizes: (sel || []).map(o => o.value) }))}
                          placeholder="All company sizes..." className="text-sm" styles={selectStyles} />
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Schedule ── */}
                <div className="col-span-2 mt-2">
                  <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Schedule</h4>
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-semibold text-[#221C51]">Start Time <span className="text-red-500">*</span></label>
                  <input type="datetime-local" name="startTime" value={formData.startTime || ''} onChange={handleInputChange}
                    className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                  {errors.startTime && <span className="text-red-500 text-xs">Start time is required</span>}
                </div>

                {!isSimpleCampaign && (
                  <div className="col-span-1">
                    <label className="block text-sm font-semibold text-[#221C51]">End Time</label>
                    <input type="datetime-local" name="endTime" value={formData.endTime || ''} onChange={handleInputChange}
                      className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                  </div>
                )}

                {/* ── Frequency Cap ── */}
                {!isSimpleCampaign && (
                  <div className="col-span-2 grid grid-cols-3 gap-4 mt-2">
                    <div>
                      <label className="block text-sm font-semibold text-[#221C51]">Frequency Cap</label>
                      <input type="number" name="frequency_cap" min="0"
                        value={formData.frequency_cap} onChange={handleInputChange}
                        placeholder="e.g. 3" className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                      <p className="text-xs text-gray-400 mt-0.5">Max impressions per user</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#221C51]">Reset Every</label>
                      <input type="number" name="frequency_cap_reset_period" min="1"
                        value={formData.frequency_cap_reset_period} onChange={handleInputChange}
                        placeholder="e.g. 7" className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#221C51]">Period</label>
                      <Select name="frequency_cap_time_unit"
                        value={[{value:'DAY',label:'Days'},{value:'WEEK',label:'Weeks'},{value:'MONTH',label:'Months'}].find(o => o.value === formData.frequency_cap_time_unit)}
                        onChange={(sel) => handleInputChange({ target: { name: 'frequency_cap_time_unit', value: sel.value } })}
                        options={[{value:'DAY',label:'Days'},{value:'WEEK',label:'Weeks'},{value:'MONTH',label:'Months'}]}
                        styles={selectStyles} className="text-sm" />
                    </div>
                  </div>
                )}

                {/* Promoted Object fields (dynamic) */}
                {promotedObjectFields.length > 0 && (
                  <div className="col-span-2 mt-2 grid grid-cols-2 gap-x-4 gap-y-4">
                    <h4 className="text-md font-semibold text-[#221C51] col-span-2">Promoted Object</h4>
                    {promotedObjectFields.map((field, i) => (
                      <div key={i} className="col-span-1">
                        <label className="block text-sm font-semibold text-[#221C51]">{field.name} {field.platform && `(${field.platform})`}</label>
                        {field.type === 'url' && <input type="url" name={field.name} placeholder={`Enter ${field.name}`} value={formData[field.name] || ''} onChange={handleInputChange} className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />}
                        {field.type === 'text' && <input type="text" name={field.name} placeholder={`Enter ${field.name}`} value={formData[field.name] || ''} onChange={handleInputChange} className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />}
                        {field.type === 'select' && <Select name={field.name} options={field.options} value={field.options.find(o => o.value === formData[field.name])} onChange={(sel) => handleSelectPromtedObjectChange(field.name, sel)} className="text-sm" styles={selectStyles} />}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── AD CREATIVE SECTION ── */}
          <div className="col-span-2 mt-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-[#221C51]">Ad</h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={showAd} onChange={() => handleToggleSection('Ad')} className="sr-only peer" />
                <div className="w-10 h-5 bg-gray-200 rounded-full peer-checked:bg-[#221C51]">
                  <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${showAd ? 'translate-x-5' : 'translate-x-0'}`}></div>
                </div>
              </label>
            </div>

            {showAd && (
              <div className="grid grid-cols-3 gap-8">
                <div className="col-span-2 space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#221C51]">AdCreative Name <span className="text-red-500">*</span></label>
                    <input type="text" name="adCreativeName" placeholder="Enter AdCreative name" value={formData.adCreativeName} onChange={handleInputChange}
                      className={`block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100${hl('adCreativeName')}`} />
                    {errors.adCreativeName && <span className="text-red-500 text-xs">Creative name is required</span>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#221C51]">Format</label>
                    <div className="flex border rounded-md">
                      {['Image', 'Video'].map(fmt => (
                        <button key={fmt} type="button"
                          className={`flex-1 py-2 text-sm font-medium ${formData.creativeType === fmt ? 'bg-[#221C51] text-white' : 'text-[#221C51] bg-white'} border-r border-[#d1d5db] last:border-none`}
                          onClick={() => setFormData({ ...formData, creativeType: fmt, videoUrl: '', videoThumbnailUrl: '', ...(fmt === 'Video' ? { imageUrl: '' } : {}) })}>
                          {fmt}
                        </button>
                      ))}
                    </div>
                  </div>

                  {formData.creativeType === 'Image' && (
                    <div>
                      <label className="block text-sm font-semibold text-[#221C51]">Image <span className="text-red-500">*</span></label>
                      <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, 'image')}
                        className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                      {errors.imageFile && <span className="text-red-500 text-xs">Image is required</span>}
                      {formData.imageUrl && <img src={formData.imageUrl} alt="preview" className="mt-2 h-24 rounded" />}

                      {/* ── AI Image Generation — only in Create with AI ── */}
                      {aiMode && (
                        <div className="mt-3 p-3 bg-[#f4f3ff] border border-[#c5bff7] rounded-lg">
                          <p className="text-xs font-bold text-[#221C51] mb-2 flex items-center gap-1">
                            <FaMagic className="text-purple-500" /> Generate with AI
                          </p>
                          <input
                            type="text"
                            placeholder="e.g. cosmetic product on clean white background, studio lighting"
                            value={aiPrompt}
                            onChange={(e) => setAiPrompt(e.target.value)}
                            className="block w-full p-2 text-xs rounded-md border border-[#c5bff7] bg-white mb-2"
                          />
                          <div className="flex gap-2 items-center">
                            <select
                              value={aiAspectRatio}
                              onChange={(e) => setAiAspectRatio(e.target.value)}
                              className="text-xs border border-[#c5bff7] rounded-md p-1.5 bg-white"
                            >
                              <option value="1:1">1:1 (square)</option>
                              <option value="4:5">4:5 (portrait)</option>
                              <option value="16:9">16:9 (landscape)</option>
                              <option value="9:16">9:16 (vertical)</option>
                            </select>
                            <button
                              type="button"
                              onClick={() => handleGenerateCreative('image')}
                              disabled={generating}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#221C51] text-white text-xs font-semibold rounded-md hover:bg-[#2d267a] disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                              <FaMagic /> {generating ? 'Generating...' : 'Generate Image'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {formData.creativeType === 'Video' && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-[#221C51]">Video <span className="text-red-500">*</span></label>
                          <input type="file" accept="video/*" onChange={(e) => handleFileUpload(e, 'video')}
                            className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                          {errors.videoFile && <span className="text-red-500 text-xs">Video is required</span>}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#221C51]">Thumbnail <span className="text-red-500">*</span></label>
                          <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, 'videoThumbnail')}
                            className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                          {errors.videoThumbnailFile && <span className="text-red-500 text-xs">Thumbnail is required</span>}
                        </div>
                      </div>

                      {/* ── AI Video Generation — only in Create with AI ── */}
                      {aiMode && (
                        <div className="p-3 bg-[#f4f3ff] border border-[#c5bff7] rounded-lg">
                          <p className="text-xs font-bold text-[#221C51] mb-2 flex items-center gap-1">
                            <FaMagic className="text-purple-500" /> Generate Video with AI
                          </p>
                          <input
                            type="text"
                            placeholder="e.g. product in motion, clean background, cinematic lighting"
                            value={aiPrompt}
                            onChange={(e) => setAiPrompt(e.target.value)}
                            className="block w-full p-2 text-xs rounded-md border border-[#c5bff7] bg-white mb-2"
                          />
                          <div className="flex gap-2 items-center">
                            <select
                              value={aiAspectRatio}
                              onChange={(e) => setAiAspectRatio(e.target.value)}
                              className="text-xs border border-[#c5bff7] rounded-md p-1.5 bg-white"
                            >
                              <option value="9:16">9:16 (TikTok / Reels)</option>
                              <option value="1:1">1:1 (square)</option>
                              <option value="16:9">16:9 (YouTube)</option>
                            </select>
                            <button
                              type="button"
                              onClick={() => handleGenerateCreative('video')}
                              disabled={generating}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#221C51] text-white text-xs font-semibold rounded-md hover:bg-[#2d267a] disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                              <FaMagic /> {generating ? 'Generating...' : 'Generate Video'}
                            </button>
                          </div>
                          <p className="text-xs text-gray-400 mt-1.5">Duration: 5 sec · Powered by Kling 3.0</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-[#221C51]">Ad Title <span className="text-red-500">*</span></label>
                    <input type="text" name="adTitle" placeholder="Enter ad title" value={formData.adTitle} onChange={handleInputChange}
                      className={`block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100${hl('adTitle')}`} />
                    {errors.adTitle && <span className="text-red-500 text-xs">Title is required</span>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#221C51]">Main Ad Text <span className="text-red-500">*</span></label>
                    <textarea name="adText" placeholder="Enter main ad text" value={formData.adText} onChange={handleInputChange} rows={3}
                      className={`block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100${hl('adText')}`} />
                    {errors.adText && <span className="text-red-500 text-xs">Ad text is required</span>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#221C51]">Description <span className="text-red-500">*</span></label>
                    <textarea name="description" placeholder="Enter description" value={formData.description} onChange={handleInputChange} rows={3}
                      className={`block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100${hl('description')}`} />
                    {errors.description && <span className="text-red-500 text-xs">Description is required</span>}
                  </div>

                  <div>
                    {(() => {
                      const ctaOptions = [
                        { value: 'LEARN_MORE', label: 'Learn More' },
                        { value: 'SHOP_NOW', label: 'Shop Now' },
                        { value: 'BUY_NOW', label: 'Buy Now' },
                        { value: 'SIGN_UP', label: 'Sign Up' },
                        { value: 'SUBSCRIBE', label: 'Subscribe' },
                        { value: 'WATCH_MORE', label: 'Watch More' },
                        { value: 'WATCH_VIDEO', label: 'Watch Video' },
                        { value: 'CONTACT_US', label: 'Contact Us' },
                        { value: 'GET_QUOTE', label: 'Get Quote' },
                        { value: 'APPLY_NOW', label: 'Apply Now' },
                        { value: 'BOOK_NOW', label: 'Book Now' },
                        { value: 'DOWNLOAD', label: 'Download' },
                        { value: 'GET_DIRECTIONS', label: 'Get Directions' },
                        { value: 'OPEN_LINK', label: 'Open Link' },
                        { value: 'ORDER_NOW', label: 'Order Now' },
                        { value: 'MESSAGE_PAGE', label: 'Message Page' },
                      ];
                      return (
                        <>
                          <label className="block text-sm font-semibold text-[#221C51]">Call to Action <span className="text-red-500">*</span></label>
                          <Select name="callToAction" options={ctaOptions}
                            value={ctaOptions.find(o => o.value === formData.callToAction) || null}
                            onChange={(sel) => handleInputChange({ target: { name: 'callToAction', value: sel.value } })}
                            className="text-sm" styles={selectStyles} />
                          {errors.callToAction && <span className="text-red-500 text-xs">Call to action is required</span>}
                        </>
                      );
                    })()}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#221C51]">Destination URL <span className="text-red-500">*</span></label>
                    <input type="text" name="destinationUrl" placeholder="https://example.com" value={formData.destinationUrl} onChange={handleInputChange}
                      className={`block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100${hl('destinationUrl')}`} />
                    {errors.destinationUrl && <span className="text-red-500 text-xs">Destination URL is required</span>}
                  </div>
                </div>

                {/* Ad Preview */}
                <div className="col-span-1">
                  <div className="p-4 border border-gray-300 rounded-lg bg-white shadow-md h-full">
                    <h4 className="text-lg font-semibold text-[#221C51] mb-2">Preview</h4>
                    {formData.imageUrl
                      ? <img src={formData.imageUrl} alt="Ad" className="w-full h-48 object-cover rounded-lg mb-2" />
                      : <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center mb-2"><FaRegImage className="text-gray-400 text-4xl" /></div>
                    }
                    <p className="text-lg font-bold text-[#221C51]">{formData.adTitle || 'Your ad title'}</p>
                    <p className="text-sm text-[#221C51] mt-1">{formData.adText || 'Main ad text appears here.'}</p>
                    <p className="text-xs text-gray-500 mt-1">{formData.description || 'Description appears here.'}</p>
                    <a href={formData.destinationUrl || '#'}
                      className="block w-full text-center px-4 py-2 mt-3 text-sm text-white bg-[#221C51] rounded-md hover:bg-[#0a0a3f]">
                      {formData.callToAction || 'Call to Action'}
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>

          {insufficientWalletWarning && (
            <div className="col-span-2 flex items-start gap-2 mt-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <FaCreditCard className="mt-0.5 flex-shrink-0" />
              <span>{insufficientWalletWarning}</span>
            </div>
          )}
          {/* ── SUBMIT ── */}
          <div className="col-span-2 flex justify-end mt-6 pt-4 border-t border-gray-200">
            <button type="button" onClick={handleSubmit} disabled={submitting}
              className="px-8 py-2.5 text-white bg-[#221C51] rounded-lg shadow-md hover:bg-[#2d267a] disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold text-sm">
              {submitting ? 'Launching campaign...' : 'Launch Campaign'}
            </button>
          </div>
        </form>
      </div>
      <ToastContainer />
    </div>
  );
};

export default CreateAd;
